import {
    Avatar,
    Button,
    Chip,
    Dropdown,
    EnhancedFormModel,
    fetchRoute,
    findNestedProperty,
    getCalendarFormat,
    Icon,
    Link,
    ListModel,
    SummaryEnhancedFormModel,
    TAccountModel,
    TagsFactory,
    TDataTableColumnsData,
    TEnhancedFormModel,
    TListModel,
    TUserModel,
    Typography
} from "@cakemail-org/ui-components-v2"
import { ERoutes } from "../../utils/url"
import { t } from "i18next"
import { Stack } from "@mui/material"
import { NameTableCol } from "../../components/tables/nameTableCol"
import { TGetListingRowsForm } from "./types"

export function buildFormScript({ form }: { form?: EnhancedFormModel | SummaryEnhancedFormModel }) {
    if (!form || !form.published_url) {
        return "Form not published yet. Please publish the form first."
    }

    return `
<script id="form-script-${form.id}">
(function() {
    // Change 'null' to match your container element ID if you want the form
    // to appear inside a specific element. 
    // If you leave this as null, the form will simply be inserted
    // right before this <script> tag in your page.
    var FORM_CONTAINER_ID = null; 
    
    var script = document.getElementById('form-script-${form.id}');
    function insertContent(content) {
        if (FORM_CONTAINER_ID) {
            var container = document.getElementById(FORM_CONTAINER_ID);
            if (container) {            
                container.innerHTML += content;
                container.id = 'form-content-${form.id}';
                return;
            }
            console.error('Container "' + FORM_CONTAINER_ID + '" not found');
            return;
        }
        
        var div = document.createElement('div');
        div.innerHTML = content;
        div.id = 'form-content-${form.id}';
        (script && script.parentNode) ? script.parentNode.insertBefore(div, script) : document.body.appendChild(div);
    }
    
    fetch('${form.published_url}').then(response => {
            if(response.status === 200) return response.text()
            return response.json()
        }).then(content => {
            if(typeof content === "object") throw content
            insertContent(content);
        }).catch(error => {
            console.error('Error loading form:', error);
        }).finally(()=>{
            if (script) script.remove();
        });
})();
</script>
${buildAdditionalFormScript({ form })}
`
}

export function buildAdditionalFormScript({ form }: { form?: EnhancedFormModel | SummaryEnhancedFormModel }) {
    if (!form?.recaptcha?.site_key) {
        return ""
    }
    return `
    <script src="https://www.recaptcha.net/recaptcha/api.js" async defer></script>
    <script>
        function onSubmit_${form?.id} (token) {
            document.querySelector("#form-content-${form?.id} form").submit();
        }
    </script>
    `
}

/*
<script>
    (function() {
        var containerId = 'form-content-${form?.id}';
        ${form?.recaptcha?.site_key ? `
        var recaptchaReady = false;
        window.onSubmit_${form?.id} = function() {
            recaptchaReady = true;
            document.querySelector('#' + containerId + ' form').dispatchEvent(new Event('submit'));
        };
        ` : ""}
        
        function init() {
            var container = document.getElementById(containerId);
            var form = container && container.querySelector('form');
            if (!form) return setTimeout(init, 500);
            
            form.onsubmit = function(e) {
                e.preventDefault();
                ${form?.recaptcha?.site_key ? `if (!recaptchaReady) return; recaptchaReady = false;` : ""}
                
                var btn = form.querySelector('[type="submit"]');
                if (btn && btn.disabled) return;
                if (btn) btn.disabled = true;
                
                fetch(form.action, { method: 'POST', body: new FormData(form) })
                    .then(function(r) { return r.json().catch(function() { return {}; }); })
                    .then(function(d) {
                        var url = d.redirect_url || d.redirectUrl || d.redirect;
                        if (url) return window.location.href = url;
                        
                        var p = document.createElement('p');
                        p.textContent = '${t("forms-submission-success")}';
                        p.style.cssText = 'text-align:center;';
                        container.insertBefore(p, container.firstChild);
                        setTimeout(function() { p.remove(); }, 3000);
                        
                        form.reset();
                        if (btn) btn.disabled = false;
                    })
                    .catch(function() {
                        if (btn) btn.disabled = false;
                    });
            };
        }
        
        document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', init) : init();
    })();
    </script>
*/

export function buildFormEmbedded({ form, htmlContent }: { form?: EnhancedFormModel | SummaryEnhancedFormModel, htmlContent?: string }) {
    if (!htmlContent) {
        return ''
    }

    // Create a temporary DOM parser to extract content
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')

    // Extract the first div content
    const firstDiv = doc.querySelector('div')
    const divContent = firstDiv ? firstDiv.outerHTML : ''

    // Extract styles from style block
    const styleBlock = doc.querySelector('style')
    const styles = styleBlock ? styleBlock.textContent || '' : ''

    // Create the new container with extracted content and styles
    const containerHtml = `
        <div class="bee-page-container" id="form-content-${form?.id}">
            <style>${styles}</style>
            ${divContent}
             ${buildAdditionalFormScript({ form })}
        </div >
        `

    return containerHtml.trim()
}

export function buildLink({ form, account, user }: { form: EnhancedFormModel | SummaryEnhancedFormModel, account: TAccountModel, user: TUserModel }) {
    return `${window.location.origin}/#${fetchRoute({
        route: ERoutes.publicForm, params: { formId: form.id }
    })}?t = ${form.published_url ? new URL(form.published_url).searchParams.get("token") : ""}& a=${account.id}& locale=${user.language} `
}


export function buildDefaultUrls({ list, account, user, form }: { list?: TListModel, account: TAccountModel, user: TUserModel, form?: TEnhancedFormModel }): { post_redirect_url: string, double_opt_in_redirect_url: string } {
    let queryParams = `?a=${account.id}&l=${form?.list_id}&locale=${user.language}&aName=${form?.name}`

    if (list) {
        queryParams += `&listName=${list.name}`
    }
    if (form?.id) {
        queryParams += `&f=${form.id}`
    }

    if (form?.double_opt_in) {
        queryParams += `&type=optin`
    } else {
        queryParams += `&type=doptin`
    }

    return {
        "post_redirect_url": window.location.origin + "/#" + fetchRoute({ route: ERoutes.listSubscription }) + queryParams,
        "double_opt_in_redirect_url": window.location.origin + "/#" + fetchRoute({ route: ERoutes.listConfirmation }) + queryParams
    }
}

export async function fetchListInfo({ listId }: { listId?: number }) {
    const list = listId ? new ListModel({ id: listId } as TListModel) : undefined
    async function fetchCustomAttributes() {
        if (list) {
            return list.getAttributes({ perPage: 200 }).then((attributes) => {
                return attributes.data
            }).catch((error) => {
                return []
            })
        }

        return []
    }
    async function fetchInterests() {
        if (list) {
            return list.getInterests({ perPage: 200 }).then((interests) => {
                return interests.data
            }).catch((error) => {
                return []
            })
        }
        return []
    }
    async function fetchTags() {
        return TagsFactory.list({ perPage: 200 }).then((tags) => {
            return tags.data
        }).catch((error) => {
            return []
        })
    }


    return Promise.all([
        fetchCustomAttributes(),
        fetchInterests(),
        fetchTags()
    ])
}

export async function updateFormLayoutOptions({
    content,
    listId,
    submissionUrl,
    recaptchaSiteKey,
    formId
}: { content: any, listId: number, submissionUrl: string, recaptchaSiteKey?: string, formId?: string | number }) {
    if (content) {
        const [customAttributes, interests, tags] = await fetchListInfo({ listId })
        const jsonForm = findNestedProperty(content, "form")

        if (!jsonForm) {
            return content
        }

        jsonForm.structure.attributes.action = submissionUrl
        jsonForm.structure.attributes.autocomplete = "on"
        jsonForm.structure.attributes.enctype = "multipart/form-data"
        jsonForm.structure.attributes.method = "post"
        jsonForm.structure.attributes.novalidate = false
        jsonForm.structure.attributes.target = "_self"

        customAttributes.forEach((attribute) => {
            const fieldInfo = jsonForm.structure.fields[attribute.name]
            jsonForm.structure.fields[attribute.name] = {
                type: fieldInfo?.type || "text",
                attributes: {
                    ...fieldInfo?.attributes,
                    name: `custom_attributes[${attribute.name}]`,
                },
                label: fieldInfo?.label || attribute.name,
                removeFromLayout: fieldInfo?.removeFromLayout !== undefined ? fieldInfo.removeFromLayout : true,
                canBeRemovedFromLayout: fieldInfo?.canBeRemovedFromLayout !== undefined ? fieldInfo.canBeRemovedFromLayout : true,
                canBeModified: fieldInfo?.canBeModified !== undefined ? fieldInfo.canBeModified : true,
                id: fieldInfo?.id || attribute.name,
                options: fieldInfo?.options || null,
            }
        })

        const interestFieldInfo = jsonForm.structure.fields["interests"]
        jsonForm.structure.fields["interests"] = {
            type: interestFieldInfo?.type || "checkbox",
            attributes: {
                ...interestFieldInfo?.attributes,
                name: "interests[]",
            },
            label: t("general-interest_other"),
            removeFromLayout: interestFieldInfo?.removeFromLayout !== undefined ? interestFieldInfo.removeFromLayout : true,
            canBeRemovedFromLayout: interestFieldInfo?.canBeRemovedFromLayout !== undefined ? interestFieldInfo.canBeRemovedFromLayout : true,
            canBeModified: interestFieldInfo?.canBeModified !== undefined ? interestFieldInfo.canBeModified : true,
            id: interestFieldInfo?.id || "interests",
            options: interestFieldInfo?.options || interests.map((interest) => ({
                label: interest.alias || interest.name,
                value: interest.name,
            })),
        }

        const tagFieldInfo = jsonForm.structure.fields["tags"]
        jsonForm.structure.fields["tags"] = {
            type: tagFieldInfo?.type || "checkbox",
            attributes: {
                ...tagFieldInfo?.attributes,
                name: "tags[]",
            },
            label: t("general-tag_other"),
            removeFromLayout: tagFieldInfo?.removeFromLayout !== undefined ? tagFieldInfo.removeFromLayout : true,
            canBeRemovedFromLayout: tagFieldInfo?.canBeRemovedFromLayout !== undefined ? tagFieldInfo.canBeRemovedFromLayout : true,
            canBeModified: tagFieldInfo?.canBeModified !== undefined ? tagFieldInfo.canBeModified : true,
            id: tagFieldInfo?.id || "tags",
            options: tagFieldInfo?.options || tags.map((tag) => ({
                label: tag.tag,
                value: tag.tag,
            })),
        }

        if (!jsonForm.structure.fields.email) {
            jsonForm.structure.fields.email = {
                attributes: {
                    required: true
                },
                canBeModified: true,
                canBeRemovedFromLayout: false,
                label: "Email",
                options: null,
                removeFromLayout: false,
                type: "email"
            }
        }

        if (!jsonForm.structure.fields.submit_button) {
            jsonForm.structure.fields.submit_button = {
                attributes: {
                    name: "submit_button",
                    value: "Submit"
                },
                canBeModified: true,
                canBeRemovedFromLayout: false,
                options: null,
                removeFromLayout: false,
                type: "submit"
            }
        }

        injectRecaptchaOptions({ content, recaptchaSiteKey, formId })

        jsonForm.structure.layout = []
        Object.keys(jsonForm.structure.fields).forEach((field) => {
            if (field === "email" || field === "submit_button") {
                return
            }
            jsonForm.structure.layout.push([field])
        })
        jsonForm.structure.layout.unshift(["email"])
        jsonForm.structure.layout.push(["submit_button"])


    }

    return content
}

export function injectRecaptchaOptions({ content, recaptchaSiteKey, formId }: { content: any, recaptchaSiteKey?: string, formId?: string | number }) {
    const jsonForm = findNestedProperty(content, "form")
    if (recaptchaSiteKey && jsonForm) {
        jsonForm.structure.fields.submit_button.attributes.class = "g-recaptcha"
        jsonForm.structure.fields.submit_button.attributes["data-action"] = "submit"
        jsonForm.structure.fields.submit_button.attributes["data-callback"] = formId ? `onSubmit_${formId}` : "onSubmit"
        jsonForm.structure.fields.submit_button.attributes["data-sitekey"] = recaptchaSiteKey

    }
    return content
}


export function getListingColsData(): () => TDataTableColumnsData[] {
    return () => [
        {
            id: "name",
            label: t("general-form_other"),
            sortable: true
        },
        {
            id: "published_on",
            label: t("general-status"),
            sortable: true
        },
        {
            id: "branding",
            label: t("general-branding"),
            sortable: true
        },
        {
            id: "list_id",
            label: t("general-list_one"),
            sortable: true
        },
        {
            id: "created_on",
            label: t("general-created_on"),
            sortable: true
        },
        {
            id: "updated_on",
            label: t("general-updated_on"),
            sortable: true
        },
        {
            id: "edited_by",
            label: t("general-edited_by"),
        },
        {
            id: "actions",
            label: ""
        }
    ]
}



export function getListingRows({
    users,
    form,
    partialList,
    handleOpenForm,
    handlePreviewForm,
    handleCopyFormInfo,
    handleRenameForm,
    handleDuplicateForm,
    handleDeleteForm,
    listMainButton,
    listActions
}: TGetListingRowsForm) {
    const isEnhanced = "enabled" in form

    const actions = <Stack direction="row" gap={3}>
        <Button
            onClick={() => { listMainButton?.onClick ? listMainButton.onClick(form) : handleOpenForm(form) }}
            size="small"
            color="body1"
            variant="outlined"
        >{listMainButton?.text || t("general-open_one")}</Button>

        <Dropdown
            size="medium"
            color="body1"
            variant="outlined"
            iconOnly
            options={
                listActions?.map((option) => {
                    return {
                        ...option,
                        onClick: () => { option.onClick(form) }
                    }
                }) ||
                [
                    {
                        isAction: true,
                        text: t("general-preview"),
                        postContent: <Icon name="Preview18" />,
                        onClick: () => { handlePreviewForm(form) }
                    },
                    {
                        isAction: true,
                        text: t("forms-copy-link"),
                        postContent: <Icon name="Copy18" />,
                        onClick: () => { handleCopyFormInfo(form, "link") }
                    },
                    {
                        isAction: true,
                        text: t("forms-copy-snippet"),
                        postContent: <Icon name="Copy18" />,
                        onClick: () => { handleCopyFormInfo(form, "snippet") }
                    },
                    {
                        isAction: true,
                        text: t("general-rename"),
                        postContent: <Icon name="Edit18" />,
                        onClick: () => { handleRenameForm(form) }
                    },
                    {
                        isAction: true,
                        text: t("general-duplicate"),
                        postContent: <Icon name="Document18" />,
                        onClick: () => { handleDuplicateForm(form) }
                    },
                    {
                        isAction: true,
                        text: t("general-delete"),
                        postContent: <Icon name="Delete18" />,
                        onClick: () => { handleDeleteForm(form) }
                    }
                ]
            }
        />
    </Stack>

    const commonInfo = {
        created_on: <Typography variant="body1L">{form.created_on !== 0 ? getCalendarFormat({ unixDate: form.created_on, showTime: true }) : ""}</Typography>,
        list_id: <Link href={"#" + fetchRoute({ route: ERoutes.contacts }) + "?listId=" + form.list_id}>{partialList?.name || form.list_id}</Link>
    }

    if (isEnhanced) {
        const editedBy = users && users[form.edited_by.id]
        const isSmartBranding = form.branding === "smart"
        return {
            name: {
                id: form.name,
                content: <NameTableCol name={form.name} desc={form.description} imgSrc={form.thumbnail_url} />
            },
            edited_by: editedBy ? <Stack direction="row" gap={3} alignItems="center">
                <Avatar variant="square" email={editedBy.email} firstName={editedBy.first_name} lastName={editedBy.last_name} />
                <Typography variant="body1L">{editedBy.first_name} {editedBy.last_name}</Typography>
            </Stack> : "-",
            published_on: <Chip label={form.published_on > 0 ? t("general-published") : t("general-unpublished")} color={form.published_on > 0 ? "secondary" : "primary"} />,
            branding: <Chip label={t(isSmartBranding ? "general-smart" : "general-basic")} color={isSmartBranding ? "shade150" : "shade150"} />,
            updated_on: <Typography variant="body1L">{form.updated_on !== 0 ? getCalendarFormat({ unixDate: form.updated_on, showTime: true }) : ""}</Typography>,
            actions,
            ...commonInfo
        }
    }

    return {
        name: {
            id: form.name,
            content: <NameTableCol name={form.name} imgSrc={form.thumbnail_url} desc={`${t("general-basic")} ${t("general-form_one")} `} />
        },
        published_on: <Chip label={t("general-published")} color={"secondary"} />,
        branding: "",
        edited_by: "",
        updated_on: "",
        actions,
        ...commonInfo
    }
}